import { useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";

const PlacesAutocomplete = ({
  value,
  onChange,
  placeholder = "Search for an address, hotel, school...",
  className = "",
  "data-testid": testId,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);

  // Use refs for init guards (so re-renders don’t break init)
  const initializedRef = useRef(false);
  const onChangeRef = useRef(onChange);

  const [, setIsInitialized] = useState(false); // optional: only for UI/debug

  // Always keep latest onChange (prevents stale closure)
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const tryInit = () => {
      if (initializedRef.current) return;
      if (!inputRef.current) return;

      const Autocomplete = window.google?.maps?.places?.Autocomplete;
      if (typeof Autocomplete !== "function") return;

      try {
        autocompleteRef.current = new Autocomplete(inputRef.current, {
          types: ["geocode", "establishment"],
          componentRestrictions: { country: "fr" },
          fields: ["formatted_address", "name", "geometry", "types"],
        });

        // ✅ IMPORTANT: store listener handle and use .remove() later
        listenerRef.current = autocompleteRef.current.addListener(
          "place_changed",
          () => {
            const place = autocompleteRef.current?.getPlace?.();
            if (!place) return;

            // --- 1. IDENTIFY THE TYPE OF PLACE ---
            const placeTypes = place.types || [];
            const isHotel = placeTypes.includes("lodging");
            const isSchool =
              placeTypes.includes("school") ||
              placeTypes.includes("university");
            const isAirport = placeTypes.includes("airport");

            // --- 2. FORMAT THE OUTPUT STRING ---
            let val = "";
            if (place?.name && place?.formatted_address) {
              // If the address already contains the name, don't duplicate it
              if (place.formatted_address.includes(place.name)) {
                val = place.formatted_address;
              } else {
                val = `${place.name}, ${place.formatted_address}`;
              }
            } else {
              // Fallback
              val = place?.formatted_address || place?.name || "";
            }

            if (!val) return;
            onChangeRef.current?.(val);
          },
        );

        initializedRef.current = true;
        setIsInitialized(true);
      } catch (e) {
        console.warn("Error initializing Google Places Autocomplete:", e);
      }
    };

    // Init now (if Google already loaded)
    tryInit();

    // If you dispatch a custom event when Google loads, keep it:
    const handleReady = () => tryInit();
    window.addEventListener("google-places-ready", handleReady);

    return () => {
      window.removeEventListener("google-places-ready", handleReady);

      // ✅ remove only our listener (no clearInstanceListeners)
      try {
        listenerRef.current?.remove?.();
      } catch {}
      listenerRef.current = null;
      autocompleteRef.current = null;
      initializedRef.current = false;
    };
  }, []);

  return (
    <Input
      ref={inputRef}
      type="text"
      value={value || ""}
      onChange={(e) => onChangeRef.current?.(e.target.value)}
      placeholder={placeholder}
      className={className}
      data-testid={testId}
      disabled={disabled}
      autoComplete="off"
    />
  );
};

export default PlacesAutocomplete;
